import { FormSection } from "@/components/common/inputs/FormSection";
import { FormattedNumberInput } from "@/components/ui/react-admin/FormattedNumberInput";
import { UsStateInput } from "@/components/common/inputs/UsStateInput";
import {
  ResourceName,
  PolicyCreate,
  type Claimant,
  type DateYMDString,
  type EndorsementType,
  type EndorsementCreateConfig,
  type LaseredClaimantCreateEntry,
  type ValidAny,
} from "@/types";
import { getInputProps } from "@/utils/helpers/columnsHelper";
import { PolicyVendorRunoutInputs } from "./PolicyVendorRunoutInputs";
import { CADENCE_CHOICES, MONTH_DAY_CHOICES, WEEKDAY_CHOICES } from "./reportingCadenceConstants";
import {
  Box,
  CircularProgress,
  FormControlLabel,
  Link,
  Stack,
  Typography,
  Switch,
  Collapse,
  InputAdornment,
  alpha,
  Autocomplete,
  TextField,
  MenuItem,
  IconButton,
  Tooltip,
} from "@mui/material";
import { CheckCircle, Add, Close } from "@mui/icons-material";
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { addDays, differenceInCalendarMonths, parseISO } from "date-fns";
import {
  AutocompleteArrayInput,
  AutocompleteInput,
  BooleanInput,
  DateInput,
  NumberInput,
  ReferenceArrayInput,
  ReferenceInput,
  SelectInput,
  TextInput,
  useGetOne,
  useGetList,
  useCreate,
  useNotify,
} from "react-admin";
import { useFormContext, useWatch, type UseFormSetValue } from "react-hook-form";
import { useAppTheme } from "@/hooks/useAppTheme";
import { useRichApiErrorNotify } from "@/hooks/useRichApiErrorNotify";
import { formatToTitleCase } from "@/utils/formatters/stringFormatters";
import {
  ENDORSEMENT_FIELD_SCHEMAS,
  hasFieldSchema,
  hasJunctionSchema,
  getDefaultConfig,
  getJunctionSchema,
  type FieldSchema,
} from "../../policies/endorsements/endorsementFieldSchemas";
import { JunctionFieldsCreate } from "../../policies/endorsements/JunctionFieldsCreate";
import { ENDORSEMENT_CATEGORIES } from "../../policies/hooks/useEndorsements";
import { getShortDescription } from "../../policies/endorsements/endorsementDescriptions";
import { validateLaseredEffectiveDate } from "@/utils/validations/laseredClaimantValidation";
import { validateContractLaser } from "@/utils/validations/contractLaserValidation";
import { AutocompleteCodeInput } from "@/components/common/fields/AutocompleteCodeInput";
import { ContractBasisInput } from "@/components/common/inputs/ContractBasisInput";
import { TerminationDateInput } from "./TerminationDateInput";
import { INCURRED_START_HELPER, PAID_END_HELPER } from "./EffectiveWindowNote";
import {
  reseedConditionWindows,
  resolveConditionWindowDefaults,
  sameConditionWindows,
  type ConditionWindows,
} from "@/utils/helpers/conditionWindowDefaults";
import { draftToRequest, useLaseredConditionDrafts } from "../hooks/useLaseredConditionDrafts";
import { LaseredConditionsEditor } from "./LaseredConditionsEditor";
import {
  LASER_KIND_OPTIONS,
  STANDING_DEDUCTIBLE_HINT,
  createLaserKindOptions,
  type LaserKind,
} from "@/utils/helpers/laserKind";

/** Shortcut for getting input props for policies */
const getPolicyInputProps = (key: string, textReplace = {}) => getInputProps(key, textReplace, ResourceName.POLICIES);

const boxLeftPadding = 3;

// Date helpers live in ./dateInputHelpers; re-exported so existing importers keep working.
export { parseDateInput, formatDateDisplay } from "./dateInputHelpers";
import { parseDateInput, formatDateDisplay } from "./dateInputHelpers";

/**
 * Calculate composite premium from enrollment/premium pairs.
 * Reused for composite monthly factor (substitute factor for premium).
 * Formula: sum(enrollment × premium) / total_enrollment
 */
export const calculateCompositePremium = (
  pairs: { enrollment?: number; premium?: number }[],
  blendedEnrolled?: number
): number | null => {
  const validPairs = pairs.filter((p) => p.enrollment && p.enrollment > 0 && p.premium && p.premium > 0);

  if (validPairs.length === 0) return null;

  const totalWeighted = validPairs.reduce((sum, p) => sum + p.enrollment! * p.premium!, 0);

  const totalEnrollment = blendedEnrolled || pairs.reduce((sum, p) => sum + (p.enrollment || 0), 0);

  if (totalEnrollment === 0) return null;

  return Math.round((totalWeighted / totalEnrollment) * 100) / 100;
};

/**
 * Variance check for the Aggregate Deductible reconciliation.
 * Returns the expected deductible (covered × monthly factor × term months)
 * and its signed delta vs the entered deductible, or null if any input is
 * missing/invalid or the delta is within $1 (rounding noise).
 *
 * Term length is computed as calendar months between the effective dates,
 * counted inclusively (Jan 1 → Dec 31 = 12 months, Jan 15 → Jul 14 = 6).
 */
export const computeDeductibleVariance = (params: {
  aggregateDeductible?: number;
  coveredComposite?: number;
  monthlyCompositeFactor?: number;
  effectiveStartDate?: string;
  effectiveEndDate?: string;
}): { expected: number; diff: number; months: number } | null => {
  const { aggregateDeductible, coveredComposite, monthlyCompositeFactor, effectiveStartDate, effectiveEndDate } =
    params;
  if (!aggregateDeductible || !coveredComposite || !monthlyCompositeFactor) return null;
  if (!effectiveStartDate || !effectiveEndDate) return null;

  const start = parseISO(effectiveStartDate);
  const end = parseISO(effectiveEndDate);
  if (isNaN(start.getTime()) || isNaN(end.getTime()) || end <= start) return null;

  // Inclusive calendar-month term: shift end by one day so Jan 1 → Dec 31 counts as 12.
  const months = differenceInCalendarMonths(addDays(end, 1), start);
  if (months <= 0) return null;

  const expected = coveredComposite * monthlyCompositeFactor * months;
  const diff = expected - aggregateDeductible;
  if (Math.abs(diff) < 1) return null;
  return { expected, diff, months };
};

/**
 * Calculate total enrollment from individual enrollment fields
 */
const calculateTotalEnrollment = (enrollments: (number | undefined)[]): number | null => {
  const validEnrollments = enrollments.filter((e): e is number => e !== undefined && e > 0);

  if (validEnrollments.length === 0) return null;

  return validEnrollments.reduce((sum, e) => sum + e, 0);
};

/**
 * Defers mounting of heavy child components until after the dialog's initial paint.
 * Shows a compact spinner placeholder so the dialog opens instantly.
 */
const DeferredSection = ({ children }: { children: React.ReactNode }) => {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    // Push mount to the next frame so the dialog renders immediately
    const id = requestAnimationFrame(() => setReady(true));
    return () => cancelAnimationFrame(id);
  }, []);

  if (!ready) {
    return (
      <Box display="flex" justifyContent="center" py={3}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  return <>{children}</>;
};

export const PolicyCreateInputs = () => {
  return (
    <Box p={2}>
      <GeneralSection />
      <DeferredSection>
        <SpecificBenefitSection />
        <AggregateBenefitSection />
        <ReportingCadenceSection />
        <LaseredClaimantsSection />
        <EndorsementsSection />
      </DeferredSection>
    </Box>
  );
};

const ReportingCadenceSection = () => {
  const cadence = useWatch({ name: "reporting_cadence" }) as string | undefined;
  const effectiveStartDate = useWatch({ name: "effective_start_date" }) as string | undefined;
  const { setValue, getValues } = useFormContext<PolicyCreate>();

  useEffect(() => {
    if (!cadence || !effectiveStartDate) return;
    const current = getValues("reporting_start_date");
    if (!current) {
      setValue("reporting_start_date", effectiveStartDate as DateYMDString);
    }
  }, [cadence, effectiveStartDate, setValue, getValues]);

  return (
    <FormSection title="Reporting Cadence" subtitle="Configure automated reporting task generation for this policy.">
      <SelectInput
        {...getPolicyInputProps("reporting_cadence")}
        choices={CADENCE_CHOICES}
        parse={(v) => (v === "" ? null : v)}
      />

      {cadence === "monthly" && (
        <SelectInput
          {...getPolicyInputProps("reporting_day_of_month")}
          choices={MONTH_DAY_CHOICES}
          parse={(v) => (v === "" || v === null || v === undefined ? null : Number(v))}
        />
      )}

      {cadence === "weekly" && (
        <SelectInput
          {...getPolicyInputProps("reporting_day_of_week")}
          choices={WEEKDAY_CHOICES}
          parse={(v) => (v === "" || v === null || v === undefined ? null : Number(v))}
        />
      )}

      <DateInput {...getPolicyInputProps("reporting_start_date")} parse={parseDateInput} format={formatDateDisplay} />

      <FormattedNumberInput {...getPolicyInputProps("task_lead_days")} displayFormat="number" defaultValue={5} />
    </FormSection>
  );
};

const GeneralSection = () => {
  const clientId = useWatch({ name: "client_id_ref" });
  const { data: clientRecord } = useGetOne(ResourceName.CLIENTS, { id: clientId }, { enabled: !!clientId });
  const { setValue, getValues } = useFormContext<PolicyCreate>();

  useEffect(() => {
    if (clientRecord) {
      const currentPolicyholder = getValues("source_data.policyholder");
      if (!currentPolicyholder && clientRecord.client_legal_name) {
        setValue("source_data.policyholder", clientRecord.client_legal_name);
      }

      if (clientRecord.partner_id_ref) {
        setValue("partner_id_ref", clientRecord.partner_id_ref);
      }
    }
  }, [clientRecord, setValue, getValues]);

  return (
    <>
      <ReferenceInput source="client_id_ref" reference={ResourceName.CLIENTS} perPage={10000} required>
        <AutocompleteInput {...getPolicyInputProps("client_id_ref")} isRequired />
      </ReferenceInput>

      <ReferenceInput source="vendor_id_ref" reference="vendors/carriers" perPage={10000} required>
        <AutocompleteInput {...getPolicyInputProps("vendor_id_ref")} optionText="company_name" isRequired />
      </ReferenceInput>

      <ReferenceArrayInput source="tpa_vendor_ids" reference="vendors/tpas" perPage={10000}>
        <AutocompleteArrayInput {...getPolicyInputProps("tpa_vendor_ids")} optionText="company_name" />
      </ReferenceArrayInput>
      <PolicyVendorRunoutInputs label="TPA" vendorIdsSource="tpa_vendor_ids" runoutSource="tpa_vendor_runout" />

      <ReferenceArrayInput source="rx_vendor_ids" reference="vendors/rx" perPage={10000}>
        <AutocompleteArrayInput {...getPolicyInputProps("rx_vendor_ids")} optionText="company_name" />
      </ReferenceArrayInput>
      <PolicyVendorRunoutInputs label="Rx" vendorIdsSource="rx_vendor_ids" runoutSource="rx_vendor_runout" />

      <ContractBasisInput {...getPolicyInputProps("contract_basis")} />
      <Stack direction="row" spacing={2}>
        <DateInput
          {...getPolicyInputProps("effective_start_date")}
          parse={parseDateInput}
          format={formatDateDisplay}
          isRequired
        />
        <DateInput
          {...getPolicyInputProps("effective_end_date")}
          parse={parseDateInput}
          format={formatDateDisplay}
          isRequired
        />
      </Stack>
      <SelectInput
        {...getPolicyInputProps("status")}
        choices={[
          { id: "active", name: "Active" },
          { id: "draft", name: "Draft" },
          { id: "in_progress", name: "In Progress" },
          { id: "completed", name: "Completed" },
          { id: "cancelled", name: "Cancelled" },
          { id: "expired", name: "Expired" },
        ]}
        defaultValue="active"
      />
      {/* #7320 / ADR-081 D5: required while status is cancelled, hidden otherwise */}
      <TerminationDateInput />
      <BooleanInput {...getPolicyInputProps("is_accel_funding_enabled")} />

      <FormSection
        title="General"
        subtitle="Enter the original policyholder details as provided by the client or carrier."
      >
        <TextInput {...getPolicyInputProps("source_data.policyholder")} isRequired />
        <TextInput {...getPolicyInputProps("source_data.policyholder_contact_name")} />
        <TextInput {...getPolicyInputProps("source_data.policyholder_contact_email")} type="email" />
        <NumberInput {...getPolicyInputProps("source_data.policyholder_tin")} />
        <TextInput {...getPolicyInputProps("source_data.policyholder_address")} />
        <UsStateInput {...getPolicyInputProps("source_data.policyholder_state")} />
        <TextInput {...getPolicyInputProps("source_data.policyholder_zip_code")} />
        <TextInput {...getPolicyInputProps("source_data.policyholder_affiliates")} />
        <UsStateInput variant="filled" {...getPolicyInputProps("source_data.state_of_issuance")} />
        <TextInput {...getPolicyInputProps("source_data.preferred_provider")} />
        {/* Carrier policy number (#5433 / ADR-057). The carrier vendor is captured
            above (vendor_id_ref); this is the carrier's own policy identifier.
            It's the resolution-confirmation / self-populating key for multi-client
            reimbursement ingestion — editing it here is the primary way ops set it
            on manually-created policies. Optional. */}
        <TextInput
          {...getPolicyInputProps("source_data.stop_loss_carrier_policy_id")}
          helperText="Carrier's policy number — used to confirm/auto-match reimbursement rows to this policy."
        />
      </FormSection>
    </>
  );
};

const SpecificBenefitSection = () => {
  const [compositePremiumManuallyEdited, setCompositePremiumManuallyEdited] = useState(false);
  const [blendedEnrolledManuallyEdited, setBlendedEnrolledManuallyEdited] = useState(false);

  // Batch all watched fields into a single subscription to minimize re-renders
  const [
    specIsDeductible,
    isAggSpecDeductible,
    employeesEnrolled,
    childrenEnrolled,
    spouseEnrolled, // TODO: Backend missing specific_employee_plus_spouse_enrolled - flag for Blake/Jarrod
    familiesEnrolled,
    blendedEnrolled,
    employeePremium,
    childrenPremium,
    spousePremium,
    familyPremium,
    topLevelContractBasis,
    effectiveStartDate,
    effectiveEndDate,
  ] = useWatch({
    name: [
      "specific_benefit.is_specific_deductible",
      "specific_benefit.is_aggregate_specific_deductible",
      "specific_benefit.individuals_enrolled",
      "specific_benefit.specific_employee_plus_children_enrolled",
      "specific_benefit.specific_employee_plus_spouse_enrolled",
      "specific_benefit.families_enrolled",
      "specific_benefit.blended_enrolled",
      "specific_benefit.specific_individual_premium",
      "specific_benefit.specific_employee_plus_children_premium",
      "specific_benefit.specific_employee_plus_spouse_premium",
      "specific_benefit.specific_family_premium",
      "contract_basis",
      "effective_start_date",
      "effective_end_date",
    ],
  });

  const { setValue } = useFormContext<PolicyCreate>();

  // Auto-populate contract basis and dates
  useEffect(() => {
    if (topLevelContractBasis && specIsDeductible) {
      setValue("specific_benefit.contract_basis", topLevelContractBasis);
    }
    if (effectiveStartDate && specIsDeductible) {
      setValue("specific_benefit.eligible_expenses_incurred_start_date", effectiveStartDate);
      setValue("specific_benefit.eligible_expenses_paid_start_date", effectiveStartDate);
    }
    if (effectiveEndDate && specIsDeductible) {
      setValue("specific_benefit.eligible_expenses_incurred_end_date", effectiveEndDate);
      setValue("specific_benefit.eligible_expenses_paid_end_date", effectiveEndDate);
    }
  }, [topLevelContractBasis, effectiveStartDate, effectiveEndDate, specIsDeductible, setValue]);

  // Auto-calculate blended enrolled unless manually edited
  useEffect(() => {
    if (blendedEnrolledManuallyEdited) return;

    const calculated = calculateTotalEnrollment([
      employeesEnrolled,
      childrenEnrolled,
      spouseEnrolled,
      familiesEnrolled,
    ]);

    if (calculated !== null) {
      setValue("specific_benefit.blended_enrolled", calculated);
    }
  }, [employeesEnrolled, childrenEnrolled, spouseEnrolled, familiesEnrolled, blendedEnrolledManuallyEdited, setValue]);

  // Auto-calculate composite premium unless manually edited
  useEffect(() => {
    if (compositePremiumManuallyEdited) return;

    const calculated = calculateCompositePremium(
      [
        { enrollment: employeesEnrolled, premium: employeePremium },
        { enrollment: childrenEnrolled, premium: childrenPremium },
        { enrollment: spouseEnrolled, premium: spousePremium },
        { enrollment: familiesEnrolled, premium: familyPremium },
      ],
      blendedEnrolled
    );

    if (calculated !== null) {
      setValue("specific_benefit.specific_composite_premium", calculated);
    }
  }, [
    employeesEnrolled,
    employeePremium,
    childrenEnrolled,
    childrenPremium,
    spouseEnrolled,
    spousePremium,
    familiesEnrolled,
    familyPremium,
    blendedEnrolled,
    compositePremiumManuallyEdited,
    setValue,
  ]);

  return (
    <FormSection
      title="Specific Benefit"
      subtitle="Define the coverage, rates, and enrollment details for specific stop-loss protection."
    >
      <BooleanInput {...getPolicyInputProps("specific_benefit.is_specific_deductible")} />

      {specIsDeductible && (
        <Box pl={boxLeftPadding} display="flex" flexDirection="column" gap={1}>
          {/* Deductibles */}
          <FormattedNumberInput
            {...getPolicyInputProps("specific_benefit.specific_deductible")}
            displayFormat="dollar"
          />

          <BooleanInput {...getPolicyInputProps("specific_benefit.is_aggregate_specific_deductible")} />
          {isAggSpecDeductible && (
            <Box pl={boxLeftPadding}>
              <FormattedNumberInput
                {...getPolicyInputProps("specific_benefit.aggregate_specific_deductible")}
                displayFormat="dollar"
              />
            </Box>
          )}

          {/* Eligible Expense Dates */}
          <Stack direction="row" spacing={2}>
            <DateInput
              {...getPolicyInputProps("specific_benefit.eligible_expenses_incurred_start_date")}
              parse={parseDateInput}
              format={formatDateDisplay}
              helperText={INCURRED_START_HELPER}
            />
            <DateInput
              {...getPolicyInputProps("specific_benefit.eligible_expenses_incurred_end_date")}
              parse={parseDateInput}
              format={formatDateDisplay}
            />
          </Stack>
          <Stack direction="row" spacing={2}>
            <DateInput
              {...getPolicyInputProps("specific_benefit.eligible_expenses_paid_start_date")}
              parse={parseDateInput}
              format={formatDateDisplay}
            />
            <DateInput
              {...getPolicyInputProps("specific_benefit.eligible_expenses_paid_end_date")}
              parse={parseDateInput}
              format={formatDateDisplay}
              helperText={PAID_END_HELPER}
            />
          </Stack>

          {/* Contract Basis */}
          <ContractBasisInput {...getPolicyInputProps("specific_benefit.contract_basis")} />

          {/* Coverage */}
          <BooleanInput {...getPolicyInputProps("specific_benefit.has_medical_coverage")} />
          <BooleanInput {...getPolicyInputProps("specific_benefit.has_rx_coverage")} />

          {/* Enrollment & Premium Pairs */}
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
            Enrollment and Monthly Specific Premium
          </Typography>

          {/* Employees (renamed from Individuals) */}
          <Stack direction="row" spacing={2} alignItems="center">
            <FormattedNumberInput
              {...getPolicyInputProps("specific_benefit.individuals_enrolled", {
                label: "Individual Employees Enrolled",
              })}
              label="Individual Employees Enrolled"
              displayFormat="number"
            />
            <FormattedNumberInput
              {...getPolicyInputProps("specific_benefit.specific_individual_premium", {
                label: "Individual Employee Premium",
              })}
              label="Individual Employee Premium"
              displayFormat="dollar"
            />
          </Stack>

          {/* Employee + Children */}
          <Stack direction="row" spacing={2} alignItems="center">
            <FormattedNumberInput
              {...getPolicyInputProps("specific_benefit.specific_employee_plus_children_enrolled")}
              displayFormat="number"
            />
            <FormattedNumberInput
              {...getPolicyInputProps("specific_benefit.specific_employee_plus_children_premium")}
              displayFormat="dollar"
            />
          </Stack>

          {/* Employee + Spouse */}
          {/* TODO: Backend missing specific_employee_plus_spouse_enrolled */}
          <Stack direction="row" spacing={2} alignItems="center">
            <FormattedNumberInput
              {...getPolicyInputProps("specific_benefit.specific_employee_plus_spouse_enrolled")}
              displayFormat="number"
            />
            <FormattedNumberInput
              {...getPolicyInputProps("specific_benefit.specific_employee_plus_spouse_premium")}
              displayFormat="dollar"
            />
          </Stack>

          {/* Families */}
          <Stack direction="row" spacing={2} alignItems="center">
            <FormattedNumberInput
              {...getPolicyInputProps("specific_benefit.families_enrolled")}
              displayFormat="number"
            />
            <FormattedNumberInput
              {...getPolicyInputProps("specific_benefit.specific_family_premium")}
              displayFormat="dollar"
            />
          </Stack>

          {/* Blended / Composite */}
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <Box flex={1}>
              <FormattedNumberInput
                {...getPolicyInputProps("specific_benefit.blended_enrolled")}
                displayFormat="number"
                onChange={() => setBlendedEnrolledManuallyEdited(true)}
                helperText={
                  blendedEnrolledManuallyEdited ? (
                    <>
                      Manually set -{" "}
                      <Link
                        component="button"
                        type="button"
                        variant="caption"
                        onClick={() => setBlendedEnrolledManuallyEdited(false)}
                      >
                        use auto-calc
                      </Link>
                    </>
                  ) : (
                    "Auto-calculated from enrollments"
                  )
                }
              />
            </Box>
            <Box flex={1}>
              <FormattedNumberInput
                {...getPolicyInputProps("specific_benefit.specific_composite_premium")}
                displayFormat="dollar"
                onChange={() => setCompositePremiumManuallyEdited(true)}
                helperText={
                  compositePremiumManuallyEdited ? (
                    <>
                      Manually set -{" "}
                      <Link
                        component="button"
                        type="button"
                        variant="caption"
                        onClick={() => setCompositePremiumManuallyEdited(false)}
                      >
                        use auto-calc
                      </Link>
                    </>
                  ) : (
                    "Auto-calculated from enrollment & premiums"
                  )
                }
              />
            </Box>
          </Stack>

          {/* Financial Limits */}
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
            Financial Limits
          </Typography>
          <FormattedNumberInput
            {...getPolicyInputProps("specific_benefit.specific_maximum_reimbursement")}
            displayFormat="dollar"
            helperText="If unlimited, leave blank"
          />
          <FormattedNumberInput
            {...getPolicyInputProps("specific_benefit.specific_stop_loss_percentage_reimbursable")}
            displayFormat="percent"
          />
        </Box>
      )}
    </FormSection>
  );
};

const AggregateBenefitSection = () => {
  const [compositeFactorManuallyEdited, setCompositeFactorManuallyEdited] = useState(false);
  const [coveredCompositeManuallyEdited, setCoveredCompositeManuallyEdited] = useState(false);
  const [enrollmentManuallyEdited, setEnrollmentManuallyEdited] = useState(false);

  // Batch all watched fields into a single subscription to minimize re-renders
  const [
    aggIsDeductible,
    specEmployeesEnrolled,
    specChildrenEnrolled,
    specSpouseEnrolled,
    specFamiliesEnrolled,
    employeesEnrolled,
    childrenEnrolled,
    spouseEnrolled,
    familiesEnrolled,
    employeeFactor,
    childrenFactor,
    spouseFactor,
    familyFactor,
    aggregateDeductible,
    coveredComposite,
    monthlyCompositeFactor,
    topLevelContractBasis,
    effectiveStartDate,
    effectiveEndDate,
  ] = useWatch({
    name: [
      "aggregate_benefit.is_aggregate_deductible",
      "specific_benefit.individuals_enrolled",
      "specific_benefit.specific_employee_plus_children_enrolled",
      "specific_benefit.specific_employee_plus_spouse_enrolled",
      "specific_benefit.families_enrolled",
      "aggregate_benefit.individuals_enrolled",
      "aggregate_benefit.aggregate_employee_plus_children_enrolled",
      "aggregate_benefit.aggregate_employee_plus_spouse_enrolled",
      "aggregate_benefit.families_enrolled",
      "aggregate_benefit.aggregate_individual_monthly_factor",
      "aggregate_benefit.aggregate_employee_plus_children_monthly_factor",
      "aggregate_benefit.aggregate_employee_plus_spouse_monthly_factor",
      "aggregate_benefit.aggregate_family_monthly_factor",
      "aggregate_benefit.aggregate_deductible",
      "aggregate_benefit.covered_composite",
      "aggregate_benefit.monthly_aggregate_spend_per_composite",
      "contract_basis",
      "effective_start_date",
      "effective_end_date",
    ],
  });

  const { setValue } = useFormContext<PolicyCreate>();

  // Auto-populate contract basis and dates
  useEffect(() => {
    if (topLevelContractBasis && aggIsDeductible) {
      setValue("aggregate_benefit.contract_basis", topLevelContractBasis);
    }
    if (effectiveStartDate && aggIsDeductible) {
      setValue("aggregate_benefit.eligible_expenses_incurred_start_date", effectiveStartDate);
      setValue("aggregate_benefit.eligible_expenses_paid_start_date", effectiveStartDate);
    }
    if (effectiveEndDate && aggIsDeductible) {
      setValue("aggregate_benefit.eligible_expenses_incurred_end_date", effectiveEndDate);
      setValue("aggregate_benefit.eligible_expenses_paid_end_date", effectiveEndDate);
    }
  }, [topLevelContractBasis, effectiveStartDate, effectiveEndDate, aggIsDeductible, setValue]);

  // Sync Specific enrollment → Aggregate enrollment unless manually edited
  useEffect(() => {
    if (enrollmentManuallyEdited || !aggIsDeductible) return;

    if (specEmployeesEnrolled !== null && specEmployeesEnrolled !== undefined)
      setValue("aggregate_benefit.individuals_enrolled", specEmployeesEnrolled);
    if (specChildrenEnrolled !== null && specChildrenEnrolled !== undefined)
      setValue("aggregate_benefit.aggregate_employee_plus_children_enrolled", specChildrenEnrolled);
    if (specSpouseEnrolled !== null && specSpouseEnrolled !== undefined)
      setValue("aggregate_benefit.aggregate_employee_plus_spouse_enrolled", specSpouseEnrolled);
    if (specFamiliesEnrolled !== null && specFamiliesEnrolled !== undefined)
      setValue("aggregate_benefit.families_enrolled", specFamiliesEnrolled);
  }, [
    specEmployeesEnrolled,
    specChildrenEnrolled,
    specSpouseEnrolled,
    specFamiliesEnrolled,
    enrollmentManuallyEdited,
    aggIsDeductible,
    setValue,
  ]);

  // Auto-calculate covered composite unless manually edited
  useEffect(() => {
    if (coveredCompositeManuallyEdited) return;

    const calculated = calculateTotalEnrollment([
      employeesEnrolled,
      childrenEnrolled,
      spouseEnrolled,
      familiesEnrolled,
    ]);

    if (calculated !== null) {
      setValue("aggregate_benefit.covered_composite", calculated);
    }
  }, [employeesEnrolled, childrenEnrolled, spouseEnrolled, familiesEnrolled, coveredCompositeManuallyEdited, setValue]);

  // Variance check: expected aggregate deductible vs entered value.
  // Shown beneath the composite row when the math doesn't reconcile.
  const deductibleVariance = useMemo(
    () =>
      computeDeductibleVariance({
        aggregateDeductible,
        coveredComposite,
        monthlyCompositeFactor,
        effectiveStartDate,
        effectiveEndDate,
      }),
    [aggregateDeductible, coveredComposite, monthlyCompositeFactor, effectiveStartDate, effectiveEndDate]
  );

  // Auto-calculate composite monthly factor (weighted by enrollment) unless manually edited.
  // Stored on the same field as the legacy "Aggregate Monthly Factor": monthly_aggregate_spend_per_composite.
  useEffect(() => {
    if (compositeFactorManuallyEdited) return;

    const calculated = calculateCompositePremium([
      { enrollment: employeesEnrolled, premium: employeeFactor },
      { enrollment: childrenEnrolled, premium: childrenFactor },
      { enrollment: spouseEnrolled, premium: spouseFactor },
      { enrollment: familiesEnrolled, premium: familyFactor },
    ]);

    if (calculated !== null) {
      setValue("aggregate_benefit.monthly_aggregate_spend_per_composite", calculated);
    }
  }, [
    employeesEnrolled,
    employeeFactor,
    childrenEnrolled,
    childrenFactor,
    spouseEnrolled,
    spouseFactor,
    familiesEnrolled,
    familyFactor,
    compositeFactorManuallyEdited,
    setValue,
  ]);

  return (
    <FormSection
      title="Aggregate Benefit"
      subtitle="Provide details for aggregate coverage, including deductible, reimbursement, and enrollment data."
    >
      <BooleanInput {...getPolicyInputProps("aggregate_benefit.is_aggregate_deductible")} />

      {aggIsDeductible && (
        <Box pl={boxLeftPadding} display="flex" flexDirection="column" gap={1}>
          {/* Deductible */}
          <FormattedNumberInput
            {...getPolicyInputProps("aggregate_benefit.aggregate_deductible")}
            displayFormat="dollar"
          />

          {/* Date Ranges */}
          <Stack direction="row" spacing={2}>
            <DateInput
              {...getPolicyInputProps("aggregate_benefit.eligible_expenses_incurred_start_date")}
              parse={parseDateInput}
              format={formatDateDisplay}
              helperText={INCURRED_START_HELPER}
            />
            <DateInput
              {...getPolicyInputProps("aggregate_benefit.eligible_expenses_incurred_end_date")}
              parse={parseDateInput}
              format={formatDateDisplay}
            />
          </Stack>
          <Stack direction="row" spacing={2}>
            <DateInput
              {...getPolicyInputProps("aggregate_benefit.eligible_expenses_paid_start_date")}
              parse={parseDateInput}
              format={formatDateDisplay}
            />
            <DateInput
              {...getPolicyInputProps("aggregate_benefit.eligible_expenses_paid_end_date")}
              parse={parseDateInput}
              format={formatDateDisplay}
              helperText={PAID_END_HELPER}
            />
          </Stack>

          {/* Contract Info */}
          <ContractBasisInput {...getPolicyInputProps("aggregate_benefit.contract_basis")} />

          {/* Coverage Flags */}
          <BooleanInput {...getPolicyInputProps("aggregate_benefit.has_medical_coverage")} />
          <BooleanInput {...getPolicyInputProps("aggregate_benefit.has_rx_coverage")} />

          {/* Monthly Aggregate Factor by Enrollment Tier */}
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
            Monthly Aggregate Factor by Enrollment Tier
          </Typography>

          {/* Individual Employees */}
          <Stack direction="row" spacing={2} alignItems="center">
            <FormattedNumberInput
              {...getPolicyInputProps("aggregate_benefit.individuals_enrolled", {
                label: "Individual Employees Enrolled",
              })}
              label="Individual Employees Enrolled"
              displayFormat="number"
              onChange={() => setEnrollmentManuallyEdited(true)}
            />
            <FormattedNumberInput
              {...getPolicyInputProps("aggregate_benefit.aggregate_individual_monthly_factor")}
              displayFormat="dollar"
            />
          </Stack>

          {/* Employee + Children */}
          <Stack direction="row" spacing={2} alignItems="center">
            <FormattedNumberInput
              {...getPolicyInputProps("aggregate_benefit.aggregate_employee_plus_children_enrolled")}
              displayFormat="number"
              onChange={() => setEnrollmentManuallyEdited(true)}
            />
            <FormattedNumberInput
              {...getPolicyInputProps("aggregate_benefit.aggregate_employee_plus_children_monthly_factor")}
              displayFormat="dollar"
            />
          </Stack>

          {/* Employee + Spouse */}
          <Stack direction="row" spacing={2} alignItems="center">
            <FormattedNumberInput
              {...getPolicyInputProps("aggregate_benefit.aggregate_employee_plus_spouse_enrolled")}
              displayFormat="number"
              onChange={() => setEnrollmentManuallyEdited(true)}
            />
            <FormattedNumberInput
              {...getPolicyInputProps("aggregate_benefit.aggregate_employee_plus_spouse_monthly_factor")}
              displayFormat="dollar"
            />
          </Stack>

          {/* Families */}
          <Stack direction="row" spacing={2} alignItems="center">
            <FormattedNumberInput
              {...getPolicyInputProps("aggregate_benefit.families_enrolled")}
              displayFormat="number"
              onChange={() => setEnrollmentManuallyEdited(true)}
            />
            <FormattedNumberInput
              {...getPolicyInputProps("aggregate_benefit.aggregate_family_monthly_factor")}
              displayFormat="dollar"
            />
          </Stack>

          {/* Composite enrollment + composite factor (both auto-calc with manual override) */}
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <Box flex={1}>
              <FormattedNumberInput
                {...getPolicyInputProps("aggregate_benefit.covered_composite")}
                displayFormat="number"
                onChange={() => setCoveredCompositeManuallyEdited(true)}
                helperText={
                  coveredCompositeManuallyEdited ? (
                    <>
                      Manually set -{" "}
                      <Link
                        component="button"
                        type="button"
                        variant="caption"
                        onClick={() => setCoveredCompositeManuallyEdited(false)}
                      >
                        use auto-calc
                      </Link>
                    </>
                  ) : (
                    "Auto-calculated from enrollments"
                  )
                }
              />
            </Box>
            <Box flex={1}>
              <FormattedNumberInput
                {...getPolicyInputProps("aggregate_benefit.monthly_aggregate_spend_per_composite")}
                displayFormat="dollar"
                onChange={() => setCompositeFactorManuallyEdited(true)}
                helperText={
                  compositeFactorManuallyEdited ? (
                    <>
                      Manually set -{" "}
                      <Link
                        component="button"
                        type="button"
                        variant="caption"
                        onClick={() => setCompositeFactorManuallyEdited(false)}
                      >
                        use auto-calc
                      </Link>
                    </>
                  ) : (
                    "Auto-calculated from enrollment & factors"
                  )
                }
              />
            </Box>
          </Stack>

          {deductibleVariance && (
            <Typography variant="caption" color="warning.main" sx={{ mt: 0.5, display: "block" }}>
              Heads up: covered composite × monthly factor × {deductibleVariance.months} months = $
              {deductibleVariance.expected.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
              , which {deductibleVariance.diff > 0 ? "exceeds" : "falls short of"} the Aggregate Deductible above by $
              {Math.abs(deductibleVariance.diff).toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
              .
            </Typography>
          )}

          {/* Aggregate Monthly Premium */}
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
            Aggregate Monthly Premium
          </Typography>
          <FormattedNumberInput
            {...getPolicyInputProps("aggregate_benefit.aggregate_composite_premium")}
            displayFormat="dollar"
          />

          {/* Financial Limits */}
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
            Financial Limits
          </Typography>
          <FormattedNumberInput
            {...getPolicyInputProps("aggregate_benefit.aggregate_stop_loss_individual_claim_limit")}
            displayFormat="dollar"
          />
          <FormattedNumberInput
            {...getPolicyInputProps("aggregate_benefit.maximum_annual_reimbursement")}
            displayFormat="dollar"
          />
          <FormattedNumberInput
            {...getPolicyInputProps("aggregate_benefit.aggregate_stop_loss_percentage_reimbursable")}
            displayFormat="percent"
          />
        </Box>
      )}
    </FormSection>
  );
};

// ─────────────────────────────────────────────────────────────
// Endorsements Section
// ─────────────────────────────────────────────────────────────

/**
 * Unified endorsements section with a consistent toggle-row + collapse pattern
 * for all endorsement types. Uses schema-driven fields from endorsementFieldSchemas.
 *
 * All types write to endorsement_configs.{type}.* and are grouped by
 * ENDORSEMENT_CATEGORIES for a clean visual hierarchy.
 */
const EndorsementsSection = () => {
  const { setValue } = useFormContext<PolicyCreate>();
  const [effectiveStartDate, effectiveEndDate] = useWatch({
    name: ["effective_start_date", "effective_end_date"],
  });

  // Track which endorsement types are enabled
  const [enabledTypes, setEnabledTypes] = useState<Set<EndorsementType>>(new Set());

  const handleToggleType = (type: EndorsementType, enabled: boolean) => {
    const updated = new Set(enabledTypes);
    if (enabled) {
      updated.add(type);
      const defaults = getDefaultConfig(type);
      const config: EndorsementCreateConfig = {
        type,
        is_enabled: true,
        valid_period_start: effectiveStartDate || null,
        valid_period_end: effectiveEndDate || null,
        config: defaults,
      };
      setValue(`endorsement_configs.${type}` as keyof PolicyCreate, config as never);
    } else {
      updated.delete(type);
      setValue(`endorsement_configs.${type}` as keyof PolicyCreate, undefined as never);
    }
    setEnabledTypes(updated);
  };

  return (
    <FormSection
      title="Endorsements"
      subtitle="Enable and configure endorsement types for this policy. Toggle a type on to set its parameters."
    >
      {ENDORSEMENT_CATEGORIES.map((category) => (
        <EndorsementCategoryGroup
          key={category.key}
          category={category}
          enabledTypes={enabledTypes}
          onToggleType={handleToggleType}
          policyStartDate={effectiveStartDate}
          policyEndDate={effectiveEndDate}
        />
      ))}
    </FormSection>
  );
};

/**
 * Renders a single category group with its endorsement type rows.
 */
const EndorsementCategoryGroup = ({
  category,
  enabledTypes,
  onToggleType,
  policyStartDate,
  policyEndDate,
}: {
  category: { key: string; label: string; types: EndorsementType[] };
  enabledTypes: Set<EndorsementType>;
  onToggleType: (type: EndorsementType, enabled: boolean) => void;
  policyStartDate?: string;
  policyEndDate?: string;
}) => {
  const { theme } = useAppTheme();

  // Render types that have a field schema or junction schema defined
  const renderableTypes = category.types.filter((type) => hasFieldSchema(type) || hasJunctionSchema(type));

  if (renderableTypes.length === 0) return null;

  return (
    <Box sx={{ mb: 2.5 }}>
      <Typography variant="body2" fontWeight={700} color="text.primary" sx={{ fontSize: "0.8125rem", mb: 1 }}>
        {category.label}
      </Typography>

      <Box
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        {renderableTypes.map((type, index) => {
          const isLast = index === renderableTypes.length - 1;
          const isEnabled = enabledTypes.has(type);

          return (
            <EndorsementToggleRow
              key={type}
              type={type}
              isEnabled={isEnabled}
              isLast={isLast}
              onToggle={(checked) => onToggleType(type, checked)}
              policyStartDate={policyStartDate}
              policyEndDate={policyEndDate}
            />
          );
        })}
      </Box>
    </Box>
  );
};

/**
 * Toggle row for an endorsement type — displays the type label with a switch,
 * expanding to show schema-driven config fields when enabled.
 */
const EndorsementToggleRow = ({
  type,
  isEnabled,
  isLast,
  onToggle,
  policyStartDate,
  policyEndDate,
}: {
  type: EndorsementType;
  isEnabled: boolean;
  isLast: boolean;
  onToggle: (checked: boolean) => void;
  policyStartDate?: string;
  policyEndDate?: string;
}) => {
  const { theme, isDarkMode } = useAppTheme();

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1.25,
          borderBottom: isEnabled || !isLast ? `1px solid ${theme.palette.divider}` : "none",
          bgcolor: isEnabled
            ? isDarkMode
              ? alpha(theme.palette.primary.main, 0.06)
              : alpha(theme.palette.primary.main, 0.03)
            : "transparent",
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="body2" fontWeight={500}>
              {formatToTitleCase(type)}
            </Typography>
            {isEnabled && <CheckCircle sx={{ fontSize: 14, color: "success.main" }} />}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25, lineHeight: 1.4 }}>
            {getShortDescription(type)}
          </Typography>
        </Box>
        <Switch size="small" checked={isEnabled} onChange={(_, checked) => onToggle(checked)} sx={{ flexShrink: 0 }} />
      </Box>

      <Collapse in={isEnabled}>
        <Box
          sx={{
            px: 2,
            py: 2,
            bgcolor: isDarkMode ? theme.palette.grey[900] : theme.palette.grey[50],
            borderBottom: !isLast ? `1px solid ${theme.palette.divider}` : "none",
          }}
        >
          <EndorsementInlineConfig type={type} policyStartDate={policyStartDate} policyEndDate={policyEndDate} />
        </Box>
      </Collapse>
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────
// Schema-driven inline config
// ─────────────────────────────────────────────────────────────

/**
 * Renders inline config fields for a single endorsement type within the policy create form.
 * Uses react-hook-form fields nested under `endorsement_configs.{type}.config.{fieldName}`.
 *
 * Valid period fields are also rendered and auto-populated from policy dates.
 */
const EndorsementInlineConfig = ({
  type,
  policyStartDate,
  policyEndDate,
}: {
  type: EndorsementType;
  policyStartDate?: string;
  policyEndDate?: string;
}) => {
  const schema = ENDORSEMENT_FIELD_SCHEMAS[type];
  const { setValue } = useFormContext() as { setValue: UseFormSetValue<PolicyCreate> };
  const junctionSchemas = useMemo(() => getJunctionSchema(type), [type]);
  const hasJunctions = junctionSchemas.length > 0;
  const getJunctionDataRef = useRef<(() => Record<string, unknown>) | null>(null);

  // Auto-sync valid period with policy dates when they change
  useEffect(() => {
    if (policyStartDate) {
      setValue(`endorsement_configs.${type}.valid_period_start` as keyof PolicyCreate, policyStartDate as never);
    }
  }, [policyStartDate, type, setValue]);

  useEffect(() => {
    if (policyEndDate) {
      setValue(`endorsement_configs.${type}.valid_period_end` as keyof PolicyCreate, policyEndDate as never);
    }
  }, [policyEndDate, type, setValue]);

  // Sync junction data into the endorsement config in form state
  const handleJunctionChange = useCallback(
    (data: Record<string, unknown>) => {
      for (const [key, value] of Object.entries(data)) {
        setValue(`endorsement_configs.${type}.config.${key}` as keyof PolicyCreate, value as never);
      }
    },
    [type, setValue]
  );

  const basePath = `endorsement_configs.${type}`;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {/* Valid period */}
      <Stack direction="row" spacing={2}>
        <DateInput
          source={`${basePath}.valid_period_start`}
          label="Valid From"
          parse={parseDateInput}
          format={formatDateDisplay}
          fullWidth
          helperText="Defaults to policy start date"
        />
        <DateInput
          source={`${basePath}.valid_period_end`}
          label="Valid Until"
          parse={parseDateInput}
          format={formatDateDisplay}
          fullWidth
          helperText="Defaults to policy end date"
        />
      </Stack>

      {/* Type-specific config fields */}
      {schema && schema.length > 0 ? (
        schema.map((field) => <InlineSchemaField key={field.source} field={field} basePath={`${basePath}.config`} />)
      ) : !hasJunctions ? (
        <Typography variant="body2" color="text.secondary" fontStyle="italic" sx={{ py: 1 }}>
          This endorsement type is event-driven only — no additional configuration fields.
        </Typography>
      ) : null}

      {/* Junction data fields (codes, conditions, tiers, entries, etc.) */}
      {hasJunctions && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ mb: 0.5, display: "block" }}>
            Details (optional)
          </Typography>
          <JunctionFieldsCreate
            endorsementType={type}
            policyId=""
            getJunctionDataRef={getJunctionDataRef}
            onStateChange={handleJunctionChange}
            compact
          />
        </Box>
      )}
    </Box>
  );
};

/**
 * Renders a single field within the inline endorsement config, using react-admin inputs.
 * Source paths are nested under the endorsement config's base path.
 */
const InlineSchemaField = ({ field, basePath }: { field: FieldSchema; basePath: string }) => {
  const source = `${basePath}.${field.source}`;
  const commonProps = {
    source,
    label: field.label,
    helperText: field.helperText,
    fullWidth: true,
    isRequired: field.required,
  };

  // Handle conditional fields
  const watchSource = field.showWhen ? `${basePath}.${field.showWhen}` : undefined;
  const watchValue = useWatch({ name: watchSource || "" });

  if (field.showWhen && !watchValue) return null;

  const inner = (() => {
    switch (field.type) {
      case "boolean":
        return <BooleanInput {...commonProps} />;
      case "number":
        return <NumberInput {...commonProps} />;
      case "dollar":
        return (
          <NumberInput
            {...commonProps}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
          />
        );
      case "percent":
        return (
          <NumberInput
            {...commonProps}
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
            format={(v: number) => (v !== null && v !== undefined ? v * 100 : "")}
            parse={(v: string) => (v !== "" ? parseFloat(v) / 100 : null)}
          />
        );
      case "text":
        return <TextInput {...commonProps} />;
      case "select":
        return <SelectInput {...commonProps} choices={field.choices ?? []} />;
      default:
        return <TextInput {...commonProps} />;
    }
  })();

  return field.showWhen ? <Box pl={boxLeftPadding}>{inner}</Box> : inner;
};

// ─────────────────────────────────────────────────────────────
// Lasered Claimants Section
// ─────────────────────────────────────────────────────────────

/**
 * Queued entries' conditional-laser windows follow the policy's defaults when
 * those move (policy term or specific-benefit dates edited after the entry was
 * added), for every date still equal to the old default (#7344). Returns the
 * same array when nothing moved.
 */
export const reseedEntryConditionWindows = (
  entries: LaseredClaimantCreateEntry[],
  prev: ConditionWindows,
  next: ConditionWindows
): LaseredClaimantCreateEntry[] => {
  let changed = false;
  const out = entries.map((e) => {
    if (!e.lasered_conditions?.length) return e;
    const conditions = e.lasered_conditions.map((c) => reseedConditionWindows(c, prev, next));
    if (conditions.every((c, i) => c === e.lasered_conditions![i])) return e;
    changed = true;
    return { ...e, lasered_conditions: conditions };
  });
  return changed ? out : entries;
};

/** A queued entry's summary: its laser type and terms. */
const entrySummary = (entry: LaseredClaimantCreateEntry): string[] => {
  const usd = (n?: number) => `$${(n ?? 0).toLocaleString()}`;
  const conditionCount = entry.lasered_conditions?.length ?? 0;
  const conditions = conditionCount ? `${conditionCount} conditional laser${conditionCount > 1 ? "s" : ""}` : "";
  switch (entry.entry_kind) {
    case "exclusion":
      return ["Excluded"];
    case "contract": {
      const cpts = entry.excluded_procedures?.length ?? 0;
      return ["Contract", usd(entry.amt_contract_lasered_deductible), cpts ? `${cpts} excluded CPT` : ""].filter(
        Boolean
      );
    }
    case "conditional":
      return [
        "Conditional",
        entry.amt_lasered_deductible > 0 ? `lasered specific deductible ${usd(entry.amt_lasered_deductible)}` : "",
        conditions,
      ].filter(Boolean);
    default:
      return ["Standard", usd(entry.amt_lasered_deductible), conditions].filter(Boolean);
  }
};

/**
 * Allows adding lasered claimants during policy creation.
 * Requires a client to be selected first (to search claimants).
 * Entries are stored in form state as `lasered_claimants[]` and
 * processed by the parent after policy creation.
 */
const LaseredClaimantsSection = () => {
  const { isDarkMode, theme } = useAppTheme();
  const { setValue } = useFormContext<PolicyCreate>();
  const clientId = useWatch({ name: "client_id_ref" });
  const policyEffectiveStart = useWatch({ name: "effective_start_date" }) as string | undefined;
  const policyEffectiveEnd = useWatch({ name: "effective_end_date" }) as string | undefined;
  // SpecificBenefit windows entered in the same create form — used to pre-fill
  // the contract-laser date pickers (Feature #5287). All editable downstream.
  const sbIncurredStart = useWatch({ name: "specific_benefit.eligible_expenses_incurred_start_date" }) as
    | string
    | undefined;
  const sbIncurredEnd = useWatch({ name: "specific_benefit.eligible_expenses_incurred_end_date" }) as
    | string
    | undefined;
  const sbPaidStart = useWatch({ name: "specific_benefit.eligible_expenses_paid_start_date" }) as string | undefined;
  const sbPaidEnd = useWatch({ name: "specific_benefit.eligible_expenses_paid_end_date" }) as string | undefined;
  const prevClientId = useRef(clientId);

  const [entries, setEntries] = useState<LaseredClaimantCreateEntry[]>([]);
  const [addingNew, setAddingNew] = useState(false);

  // Clear entries when the client changes (claimants are client-scoped)
  useEffect(() => {
    if (prevClientId.current && prevClientId.current !== clientId) {
      setEntries([]);
      setAddingNew(false);
    }
    prevClientId.current = clientId;
  }, [clientId]);

  // Sync entries to form state
  useEffect(() => {
    setValue("lasered_claimants", entries.length > 0 ? entries : undefined);
  }, [entries, setValue]);

  // When the parent policy's effective_start_date changes, reset effective_date on
  // all existing entries to the new policy default — parent context drives the date.
  const prevPolicyStart = useRef(policyEffectiveStart);
  useEffect(() => {
    if (prevPolicyStart.current === policyEffectiveStart) return;
    setEntries((prev) => prev.map((e) => ({ ...e, effective_date: policyEffectiveStart || undefined })));
    prevPolicyStart.current = policyEffectiveStart;
  }, [policyEffectiveStart]);

  // Same for the queued entries' conditional-laser windows (dates the user did
  // not change follow the new defaults).
  const conditionWindows = useMemo(
    () =>
      resolveConditionWindowDefaults({
        specificBenefit: {
          eligible_expenses_incurred_start_date: sbIncurredStart,
          eligible_expenses_incurred_end_date: sbIncurredEnd,
          eligible_expenses_paid_start_date: sbPaidStart,
          eligible_expenses_paid_end_date: sbPaidEnd,
        },
        policyStart: policyEffectiveStart,
        policyEnd: policyEffectiveEnd,
      }).windows,
    [sbIncurredStart, sbIncurredEnd, sbPaidStart, sbPaidEnd, policyEffectiveStart, policyEffectiveEnd]
  );
  const prevConditionWindows = useRef(conditionWindows);
  useEffect(() => {
    const prev = prevConditionWindows.current;
    prevConditionWindows.current = conditionWindows;
    if (sameConditionWindows(prev, conditionWindows)) return;
    setEntries((current) => reseedEntryConditionWindows(current, prev, conditionWindows));
  }, [conditionWindows]);

  // Fetch claimants for the selected client
  const { data: claimants = [] } = useGetList<Claimant>(
    ResourceName.CLAIMANTS,
    {
      filter: clientId ? { client_id_ref: clientId } : {},
      pagination: { page: 1, perPage: 500 },
      sort: { field: "claimant_name", order: "ASC" },
    },
    { enabled: !!clientId }
  );

  // Filter out already-added claimants
  const addedIds = new Set(entries.map((e) => e.claimant_id_ref));
  const availableClaimants = claimants.filter((c) => !addedIds.has(String(c.id)));

  const handleAddEntry = useCallback((entry: LaseredClaimantCreateEntry) => {
    setEntries((prev) => [...prev, entry]);
    setAddingNew(false);
  }, []);

  const handleRemoveEntry = useCallback((claimantId: string) => {
    setEntries((prev) => prev.filter((e) => e.claimant_id_ref !== claimantId));
  }, []);

  if (!clientId) {
    return (
      <FormSection title="Lasered Claimants" subtitle="Select a client above to add lasered claimants.">
        <Typography variant="body2" color="text.disabled" sx={{ py: 2 }}>
          Select a client to see available claimants.
        </Typography>
      </FormSection>
    );
  }

  return (
    <FormSection
      title="Lasered Claimants"
      subtitle="Designate claimants as lasered for this policy. You can also add them after creation from the policy page."
    >
      {/* Existing entries */}
      {entries.length > 0 && (
        <Stack spacing={1} sx={{ mb: 2 }}>
          {entries.map((entry) => (
            <Box
              key={entry.claimant_id_ref}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 2,
                py: 1,
                borderRadius: 1.5,
                border: `1px solid ${theme.palette.divider}`,
                bgcolor: isDarkMode ? alpha(theme.palette.background.paper, 0.6) : alpha(theme.palette.grey[50], 0.8),
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center" flex={1}>
                <Typography variant="body2" fontWeight={600} sx={{ minWidth: 160 }}>
                  {entry.claimant_display_name}
                </Typography>
                {entrySummary(entry).map((part) => (
                  <Typography key={part} variant="caption" color="text.secondary">
                    {part}
                  </Typography>
                ))}
              </Stack>
              <Tooltip title="Remove">
                <IconButton
                  size="small"
                  onClick={() => handleRemoveEntry(entry.claimant_id_ref)}
                  sx={{ color: "text.secondary" }}
                >
                  <Close fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          ))}
        </Stack>
      )}

      {/* Add new row (inline form) */}
      {addingNew ? (
        <LaseredClaimantInlineForm
          claimants={availableClaimants}
          clientId={clientId}
          policyEffectiveStart={policyEffectiveStart}
          policyEffectiveEnd={policyEffectiveEnd}
          contractDefaults={{
            incurredStart: sbIncurredStart,
            incurredEnd: sbIncurredEnd,
            paidStart: sbPaidStart,
            paidEnd: sbPaidEnd,
          }}
          onAdd={handleAddEntry}
          onCancel={() => setAddingNew(false)}
        />
      ) : (
        <Box>
          <Link
            component="button"
            type="button"
            variant="body2"
            onClick={() => setAddingNew(true)}
            sx={{ display: "flex", alignItems: "center", gap: 0.5, cursor: "pointer" }}
          >
            <Add fontSize="small" /> Add lasered claimant
          </Link>
        </Box>
      )}
    </FormSection>
  );
};

/** Inline form row for adding a new lasered claimant entry. */
const LaseredClaimantInlineForm = ({
  claimants,
  clientId,
  policyEffectiveStart,
  policyEffectiveEnd,
  contractDefaults,
  onAdd,
  onCancel,
}: {
  claimants: Claimant[];
  clientId: string;
  policyEffectiveStart?: string;
  policyEffectiveEnd?: string;
  /** SpecificBenefit windows from the same create form — pre-fill the contract dates. */
  contractDefaults: {
    incurredStart?: string;
    incurredEnd?: string;
    paidStart?: string;
    paidEnd?: string;
  };
  onAdd: (entry: LaseredClaimantCreateEntry) => void;
  onCancel: () => void;
}) => {
  const { theme } = useAppTheme();
  const [create] = useCreate();
  const notify = useNotify();
  const notifyError = useRichApiErrorNotify();

  const [selectedClaimant, setSelectedClaimant] = useState<Claimant | null>(null);
  const [isCreatingNew, setIsCreatingNew] = useState(true);
  const [newFirstName, setNewFirstName] = useState("");
  const [newLastName, setNewLastName] = useState("");
  const [newMemberId, setNewMemberId] = useState("");
  const [kind, setKind] = useState<LaserKind>("standard");
  const [isExclusion, setIsExclusion] = useState(false);
  const [amtDeductible, setAmtDeductible] = useState("");
  const [effectiveDate, setEffectiveDate] = useState<string>(policyEffectiveStart ?? "");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ── Contract-laser fields (Feature #5287) — pre-filled from the SpecificBenefit
  //    windows entered in the same create form; all editable. ──
  const [contractDeductible, setContractDeductible] = useState("");
  const [contractIncurredStart, setContractIncurredStart] = useState(contractDefaults.incurredStart ?? "");
  const [contractIncurredEnd, setContractIncurredEnd] = useState(contractDefaults.incurredEnd ?? "");
  const [contractPaidStart, setContractPaidStart] = useState(contractDefaults.paidStart ?? "");
  const [contractPaidEnd, setContractPaidEnd] = useState(contractDefaults.paidEnd ?? "");
  const [contractExcludedCpts, setContractExcludedCpts] = useState<string[]>([]);

  // Always reset to the policy's start when the parent context's start date changes.
  useEffect(() => {
    setEffectiveDate(policyEffectiveStart ?? "");
  }, [policyEffectiveStart]);

  // Re-seed the contract date windows when the parent SpecificBenefit dates change.
  useEffect(() => {
    setContractIncurredStart(contractDefaults.incurredStart ?? "");
    setContractIncurredEnd(contractDefaults.incurredEnd ?? "");
    setContractPaidStart(contractDefaults.paidStart ?? "");
    setContractPaidEnd(contractDefaults.paidEnd ?? "");
  }, [
    contractDefaults.incurredStart,
    contractDefaults.incurredEnd,
    contractDefaults.paidStart,
    contractDefaults.paidEnd,
  ]);

  // Inline contract-laser validation — only when Contract is selected.
  const contractLaserError =
    kind === "contract"
      ? validateContractLaser({
          isExcluded: isExclusion,
          incurredStart: contractIncurredStart,
          incurredEnd: contractIncurredEnd,
          paidStart: contractPaidStart,
          paidEnd: contractPaidEnd,
          deductible: contractDeductible,
        })
      : "";

  // The straight laser: Standard's deductible, or Conditional's optional standing deductible.
  const amountSet = amtDeductible.trim() !== "";
  const amount = parseFloat(amtDeductible);
  const writesStraight = kind === "standard" || (kind === "conditional" && amountSet);
  const amountError =
    amountSet && !(amount > 0)
      ? kind === "conditional"
        ? "Must be greater than $0, or leave it blank."
        : "Must be greater than $0."
      : "";
  const effectiveDateError = writesStraight
    ? validateLaseredEffectiveDate({
        laserType: "individual",
        effectiveDate,
        policyStart: policyEffectiveStart,
        policyEnd: policyEffectiveEnd,
      })
    : "";

  // Conditional lasers (Feature #3502, #7344). Neither the policy nor the laser
  // exists yet, so rows ride on the entry and are written after create.
  const conditionWindowDefaults = useMemo(
    () =>
      resolveConditionWindowDefaults({
        specificBenefit: {
          eligible_expenses_incurred_start_date: contractDefaults.incurredStart,
          eligible_expenses_incurred_end_date: contractDefaults.incurredEnd,
          eligible_expenses_paid_start_date: contractDefaults.paidStart,
          eligible_expenses_paid_end_date: contractDefaults.paidEnd,
        },
        policyStart: policyEffectiveStart,
        policyEnd: policyEffectiveEnd,
      }),
    [
      contractDefaults.incurredStart,
      contractDefaults.incurredEnd,
      contractDefaults.paidStart,
      contractDefaults.paidEnd,
      policyEffectiveStart,
      policyEffectiveEnd,
    ]
  );
  const conditionDrafts = useLaseredConditionDrafts({ policyId: "", defaults: conditionWindowDefaults, active: true });
  // Row errors show in the editor and disable Add; "none yet" is said on Add.
  const conditionsError = kind === "conditional" ? conditionDrafts.error : "";
  const noConditions = kind === "conditional" && conditionDrafts.drafts.length === 0;

  const claimantDisplayName = useCallback((c: Claimant) => {
    const parts = [c.claimant_last_name, c.claimant_first_name].filter(Boolean);
    const name = parts.join(", ") || c.claimant_name || "Unknown";
    return c.member_id ? `${name} (${c.member_id})` : name;
  }, []);

  const handleSubmit = async () => {
    const validationError =
      (kind === "standard" && !amountSet ? "Enter the deductible." : "") ||
      amountError ||
      effectiveDateError ||
      contractLaserError ||
      conditionsError ||
      (noConditions ? "Add at least one conditional laser." : "");
    if (validationError) {
      notify(validationError, { type: "warning" });
      return;
    }

    let claimantId: string;
    let displayName: string;

    if (isCreatingNew) {
      if (!newFirstName.trim() || !newLastName.trim()) return;
      setIsSubmitting(true);
      try {
        const payload: Record<string, string> = {
          claimant_first_name: newFirstName.trim(),
          claimant_last_name: newLastName.trim(),
          client_id_ref: clientId,
        };
        if (newMemberId.trim()) payload.member_id = newMemberId.trim();
        const result = await create(ResourceName.CLAIMANTS, { data: payload }, { returnPromise: true });
        claimantId = String((result as ValidAny).data?.id ?? (result as ValidAny).id);
        displayName = `${newFirstName.trim()} ${newLastName.trim()}`;
      } catch (err) {
        notifyError(err);
        setIsSubmitting(false);
        return;
      }
      setIsSubmitting(false);
    } else {
      if (!selectedClaimant) return;
      claimantId = String(selectedClaimant.id);
      displayName = claimantDisplayName(selectedClaimant);
    }

    // Contract laser / full exclusion: PUT to the contract-laser endpoint after
    // policy creation; the straight-laser fields stay at their neutral defaults.
    if (kind === "contract") {
      onAdd(
        isExclusion
          ? {
              claimant_id_ref: claimantId,
              claimant_display_name: displayName,
              entry_kind: "exclusion",
              laser_type: "individual",
              amt_lasered_deductible: 0,
              amt_incremental_lasered_deductible: 0,
            }
          : {
              claimant_id_ref: claimantId,
              claimant_display_name: displayName,
              entry_kind: "contract",
              laser_type: "individual",
              amt_lasered_deductible: 0,
              amt_incremental_lasered_deductible: 0,
              amt_contract_lasered_deductible: parseFloat(contractDeductible) || 0,
              contract_incurred_start_date: contractIncurredStart as DateYMDString,
              contract_incurred_end_date: contractIncurredEnd as DateYMDString,
              contract_paid_start_date: contractPaidStart as DateYMDString,
              contract_paid_end_date: contractPaidEnd as DateYMDString,
              excluded_procedures: contractExcludedCpts.map((s) => s.trim().toUpperCase()).filter(Boolean),
            }
      );
      return;
    }

    // Standard, or Conditional (its conditional lasers, after an optional standing deductible).
    onAdd({
      claimant_id_ref: claimantId,
      claimant_display_name: displayName,
      entry_kind: kind === "conditional" ? "conditional" : "straight",
      laser_type: "individual",
      amt_lasered_deductible: writesStraight ? amount : 0,
      amt_incremental_lasered_deductible: 0,
      ...(writesStraight && effectiveDate && { effective_date: effectiveDate }),
      ...(kind === "conditional" && { lasered_conditions: conditionDrafts.drafts.map(draftToRequest) }),
    });
  };

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 1.5,
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: theme.palette.background.paper,
      }}
    >
      <Stack spacing={2}>
        {/* Claimant selection or inline create */}
        {!isCreatingNew ? (
          <Box>
            <Autocomplete
              options={claimants}
              getOptionLabel={claimantDisplayName}
              value={selectedClaimant}
              onChange={(_e, val) => setSelectedClaimant(val)}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              size="small"
              renderInput={(params) => <TextField {...params} label="Claimant" placeholder="Search claimants..." />}
            />
            <Link
              component="button"
              type="button"
              variant="caption"
              onClick={() => {
                setIsCreatingNew(true);
                setSelectedClaimant(null);
              }}
              sx={{ mt: 0.5, cursor: "pointer" }}
            >
              or create a new claimant
            </Link>
          </Box>
        ) : (
          <Box>
            <Stack direction="row" spacing={2}>
              <TextField
                label="First Name"
                required
                size="small"
                fullWidth
                value={newFirstName}
                onChange={(e) => setNewFirstName(e.target.value)}
              />
              <TextField
                label="Last Name"
                required
                size="small"
                fullWidth
                value={newLastName}
                onChange={(e) => setNewLastName(e.target.value)}
              />
              <TextField
                label="Member ID"
                size="small"
                fullWidth
                value={newMemberId}
                onChange={(e) => setNewMemberId(e.target.value)}
              />
            </Stack>
            <Link
              component="button"
              type="button"
              variant="caption"
              onClick={() => {
                setIsCreatingNew(false);
                setNewFirstName("");
                setNewLastName("");
                setNewMemberId("");
              }}
              sx={{ mt: 0.5, cursor: "pointer" }}
            >
              or select an existing claimant
            </Link>
          </Box>
        )}

        {/* Laser type (PO 2026-10-07): one per claimant. */}
        <TextField
          label="Laser Type"
          select
          fullWidth
          size="small"
          value={kind}
          onChange={(e) => setKind(e.target.value as LaserKind)}
          helperText={LASER_KIND_OPTIONS.find((o) => o.value === kind)?.description}
        >
          {createLaserKindOptions().map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.title}
            </MenuItem>
          ))}
        </TextField>

        {(kind === "standard" || kind === "conditional") && (
          <>
            {kind === "conditional" && (
              <Typography variant="caption" color="text.secondary">
                {STANDING_DEDUCTIBLE_HINT}
              </Typography>
            )}
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <TextField
                label="Lasered Specific Deductible"
                type="number"
                size="small"
                fullWidth
                required={kind === "standard"}
                value={amtDeductible}
                onChange={(e) => setAmtDeductible(e.target.value)}
                error={!!amountError}
                helperText={amountError || (kind === "conditional" ? "Optional" : undefined)}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              />
              <TextField
                label="Effective Date"
                type="date"
                fullWidth
                required={writesStraight}
                size="small"
                value={effectiveDate}
                onChange={(e) => setEffectiveDate(e.target.value)}
                error={!!effectiveDateError}
                helperText={
                  effectiveDateError ||
                  (policyEffectiveStart && policyEffectiveEnd
                    ? `Policy: ${policyEffectiveStart} to ${policyEffectiveEnd}`
                    : undefined)
                }
                slotProps={{
                  htmlInput: { min: policyEffectiveStart, max: policyEffectiveEnd },
                  inputLabel: { shrink: true },
                }}
              />
            </Stack>
          </>
        )}

        {/* Contract laser fields (Feature #5287) — date windows pre-filled from
            the SpecificBenefit entered above, all editable; CPT exclusions and an
            elevated deductible. Or a full exclusion, which needs no terms. */}
        {kind === "contract" && (
          <>
            <Box>
              <FormControlLabel
                control={
                  <Switch checked={isExclusion} onChange={(e) => setIsExclusion(e.target.checked)} size="small" />
                }
                label="Fully exclude this claimant from the contract"
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                {isExclusion
                  ? "The claimant is carved out of the contract entirely (no specific/aggregate eligibility). No additional terms are required."
                  : "An elevated specific deductible scoped to a contract period. Only claims incurred AND paid inside both windows whose CPT is not excluded count toward it."}
              </Typography>
            </Box>
            {!isExclusion && (
              <>
                <Stack direction="row" spacing={2}>
                  <TextField
                    label="Incurred Start"
                    type="date"
                    fullWidth
                    size="small"
                    value={contractIncurredStart}
                    onChange={(e) => setContractIncurredStart(e.target.value)}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                  <TextField
                    label="Incurred End"
                    type="date"
                    fullWidth
                    size="small"
                    value={contractIncurredEnd}
                    onChange={(e) => setContractIncurredEnd(e.target.value)}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                </Stack>
                <Stack direction="row" spacing={2}>
                  <TextField
                    label="Paid Start"
                    type="date"
                    fullWidth
                    size="small"
                    value={contractPaidStart}
                    onChange={(e) => setContractPaidStart(e.target.value)}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                  <TextField
                    label="Paid End"
                    type="date"
                    fullWidth
                    size="small"
                    value={contractPaidEnd}
                    onChange={(e) => setContractPaidEnd(e.target.value)}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                </Stack>
                <AutocompleteCodeInput
                  codeType="cpt"
                  value={contractExcludedCpts}
                  onChange={setContractExcludedCpts}
                  label="Excluded Procedures (CPT)"
                  placeholder="Search by code or description (e.g., office visit)"
                />
                <TextField
                  label="Contract-Lasered Deductible"
                  type="number"
                  fullWidth
                  size="small"
                  value={contractDeductible}
                  onChange={(e) => setContractDeductible(e.target.value)}
                  error={!!contractLaserError}
                  helperText={contractLaserError || undefined}
                  InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                />
              </>
            )}
          </>
        )}

        {kind === "conditional" && (
          <LaseredConditionsEditor drafts={conditionDrafts} defaults={conditionWindowDefaults} />
        )}

        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Link component="button" type="button" variant="body2" onClick={onCancel} sx={{ cursor: "pointer" }}>
            Cancel
          </Link>
          {(() => {
            const disabled =
              isSubmitting || !!amountError || !!effectiveDateError || !!contractLaserError || !!conditionsError;
            return (
              <Link
                component="button"
                type="button"
                variant="body2"
                disabled={disabled}
                onClick={handleSubmit}
                sx={{
                  cursor: disabled ? "not-allowed" : "pointer",
                  fontWeight: 600,
                  opacity: disabled ? 0.5 : 1,
                }}
              >
                {isSubmitting ? "Creating..." : "Add"}
              </Link>
            );
          })()}
        </Stack>
      </Stack>
    </Box>
  );
};
